// imports
import { useStripe } from '@stripe/react-stripe-js';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Chip, CircularProgress, Radio, Typography } from '@mui/material';
import { FC, forwardRef, useContext, useEffect, useImperativeHandle, useState } from 'react';

// components
import { Alert } from 'components/common/Alert';
import { CardMenu } from 'components/common/CardMenu';

// interfaces, constants, helper
import {
  ADD_NEW_PAYMENT_METHOD_TEXT,
  CHANGE_PAYMENT_METHOD_TEXT,
  DEFAULT_TEXT,
  PAYMENT_METHOD_NOT_FOUND,
  PLEASE_SELECT_A_CARD,
} from 'constants/constant';
import { SECONDARY_FONT } from 'constants/fonts';
import { CardTypes, HTTP_STATUS, STAFF_ROUTE } from 'constants/index';
import { CheckoutContext } from 'contexts/CheckoutContext';
import { UserSubscriptionsContext } from 'contexts/UserSubscriptions';
import {
  StripePaymentMethod,
  StripePaymentMethodType,
  Subscription,
  useActiveStaffSubscriptionMutation,
  useChangeSubscriptionPlanMutation,
  useCreateStaffMutation,
  useCreateSubscriptionMutation,
  useGetPaymentMethodsLazyQuery,
  useGetStaffSubscriptionLazyQuery,
} from 'generated/graphql';
import { ExistingCardFormType, HandleConfirmPaymentProps, ModeType, Nullable } from 'interfaces';
import { getCardIcon, handleLogout } from 'lib/helper';
import { ActionType } from 'reducer/checkoutReducer';
import { CreditCardBox } from 'styles/commonComponentStyle';
import { flexCenter } from 'theme/styleConstant';

const ExistingCards: FC<ExistingCardFormType> = forwardRef(
  (
    {
      setSelectedCard,
      selectedCard,
      setFormLoading,
      priceId,
      openAddCardForm,
      mode,
      staff,
      onComplete,
      isTrialEnded,
    },
    ref,
  ) => {
    const stripe = useStripe();
    const navigate = useNavigate();
    const { dispatch } = useContext(CheckoutContext);
    const { userSubscription, refetchSubscription } = useContext(UserSubscriptionsContext);
    const [staffSubscription, setStaffSubscription] = useState<Nullable<Subscription>>(null);

    const params = useParams();
    const { id: staffId } = params;

    const [fetchPaymentMethods, { data, loading, refetch }] = useGetPaymentMethodsLazyQuery({
      onError: (error) => Alert.error(error.message),
    });

    const { getPaymentMethods } = data || {};
    const { paymentMethods } = getPaymentMethods || {};

    const handleSuccess = () => {
      refetch();
    };

    const handleConfirmPayment = async ({
      url,
      status,
      message,
      clientSecret,
    }: HandleConfirmPaymentProps) => {
      if (status !== HTTP_STATUS.SUCCESS) {
        Alert.error(message ?? '');
      } else if (clientSecret) {
        const confirmPayment =
          selectedCard?.type === StripePaymentMethodType.Card
            ? await stripe?.confirmCardPayment(clientSecret, {
                setup_future_usage: 'off_session',
              })
            : await stripe?.confirmUsBankAccountPayment(clientSecret);

        const { error } = confirmPayment || {};
        if (error) {
          const { message } = error;
          Alert.error(message ?? '');
          setFormLoading(false);
          if (url) {
            navigate(url);
          }
          return;
        }
      } else {
        Alert.success(message || '');
        await refetchSubscription();
        if (!staffId) handleLogout();
        onComplete?.();
      }
    };

    const [getStaffSubscription] = useGetStaffSubscriptionLazyQuery({
      onCompleted: (data) => {
        const { getStaffSubscription } = data || {};
        const { subscription, response } = getStaffSubscription || {};
        const { status, message } = response || {};

        if (status === HTTP_STATUS.SUCCESS) {
          setStaffSubscription(subscription as Subscription);
        } else {
          Alert.error(message ?? '');
        }
      },
      onError: (error) => {
        Alert.error(error.message);
      },
    });

    const [createStaff] = useCreateStaffMutation({
      onCompleted: async (data) => {
        const { createStaff } = data;
        const { response, clientSecret } = createStaff || {};
        const { message, status } = response || {};

        await handleConfirmPayment({ clientSecret, url: STAFF_ROUTE, message, status });
        dispatch({ type: ActionType.RESET });
        setFormLoading(false);
        navigate(STAFF_ROUTE);
      },

      onError: ({ message }) => {
        Alert.error(message);
        setFormLoading(false);
      },
    });

    const [activateStaff] = useActiveStaffSubscriptionMutation({
      onCompleted: async (data) => {
        const { activeStaffSubscription } = data;
        const { response, clientSecret } = activeStaffSubscription || {};
        const { message, status } = response || {};

        await handleConfirmPayment({ clientSecret, url: STAFF_ROUTE, message, status });
        dispatch({ type: ActionType.RESET });
        setFormLoading(false);
        navigate(STAFF_ROUTE);
      },

      onError: ({ message }) => {
        Alert.error(message);
        setFormLoading(false);
        dispatch({ type: ActionType.RESET });
      },
    });

    const [createStripeSubscription] = useCreateSubscriptionMutation({
      onCompleted: async (data) => {
        const { createSubscription } = data;
        const { response, clientSecret } = createSubscription || {};
        const { status, message } = response || {};
        await handleConfirmPayment({ clientSecret, message, status });
        if (clientSecret) {
          setTimeout(() => {
            dispatch({ type: ActionType.RESET });
            handleLogout();
            setFormLoading(false);
          }, 3000);
        } else {
          setFormLoading(false);
          dispatch({ type: ActionType.RESET });
        }
      },
      onError: ({ message }) => {
        Alert.error(message);
        setFormLoading(false);
        dispatch({ type: ActionType.RESET });
      },
    });

    const [changeSubscriptionPlan] = useChangeSubscriptionPlanMutation({
      onCompleted: async (data) => {
        const { changeSubscriptionPlan } = data;
        const { response, clientSecret } = changeSubscriptionPlan || {};
        const { message, status } = response || {};
        await handleConfirmPayment({ clientSecret, message, status });

        setTimeout(() => {
          if (!staffId) {
            handleLogout();
          } else {
            navigate(STAFF_ROUTE);
          }
          setFormLoading(false);
          dispatch({ type: ActionType.RESET });
        }, 1000);
      },
      onError: ({ message }) => {
        Alert.error(message);
        setFormLoading(false);
      },
    });

    const handleCardSelect = (card: StripePaymentMethod) => () => {
      setSelectedCard(card);
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        const currentSubscription = staffId ? staffSubscription : userSubscription;
        setFormLoading(true);

        // First-time subscribe (CREATE_ADMIN): allow trial without payment unless trial ended (re-subscribe must pay)
        if (mode === ModeType.CREATE_ADMIN && priceId) {
          if (isTrialEnded && !selectedCard?.id) {
            Alert.error(PLEASE_SELECT_A_CARD);
            setFormLoading(false);
            return;
          }
          await createStripeSubscription({
            variables: {
              createSubscriptionInput: {
                pricingId: priceId,
                ...(selectedCard?.id && { paymentIntent: selectedCard.id }),
              },
            },
          });
          return;
        }

        if (priceId && selectedCard) {
          if (mode === ModeType.UPGRADE) {
            await changeSubscriptionPlan({
              variables: {
                changeSubscriptionPlanInput: {
                  newPricingId: priceId,
                  subscriptionId: currentSubscription?.id || '',
                },
              },
            });
          }

          if (mode === ModeType.CREATE_STAFF && staff) {
            const { email, lastName, password, firstName, role, phone } = staff || {};
            await createStaff({
              variables: {
                createStaffInput: {
                  email: email?.toLowerCase() ?? '',
                  lastName: lastName ?? '',
                  password: password ?? '',
                  firstName: firstName ?? '',
                  staffRole: role ?? '',
                  ...(phone && { phone: phone }),
                  paymentIntent: selectedCard.id,
                  priceId: priceId,
                },
              },
            });
          }

          if (mode === ModeType.ACTIVATE_STAFF && staffId) {
            await activateStaff({
              variables: {
                activeStaffSubscriptionInput: {
                  id: staffId,
                  priceId: priceId,
                  paymentIntent: selectedCard.id,
                },
              },
            });
          }
        } else {
          Alert.error(PLEASE_SELECT_A_CARD);
        }
      },
      refetch: () => {
        refetch();
      },
    }));

    useEffect(() => {
      if (staffId && mode === ModeType.UPGRADE) {
        getStaffSubscription({
          variables: {
            getSubscriptionInput: {
              userId: staffId,
            },
          },
        });
      }
    }, [getStaffSubscription, mode, staffId]);

    useEffect(() => {
      fetchPaymentMethods();
    }, [fetchPaymentMethods]);

    if (loading)
      return (
        <Box {...flexCenter} p={5}>
          <CircularProgress />
        </Box>
      );

    if (!paymentMethods || paymentMethods.length === 0)
      return (
        <Box px={5}>
          <Typography variant="h6" ml={2}>
            {PAYMENT_METHOD_NOT_FOUND}
          </Typography>
          <Box mt={2}>
            <Button variant="text" color="primary" size="large" onClick={openAddCardForm}>
              {ADD_NEW_PAYMENT_METHOD_TEXT}
            </Button>
          </Box>
        </Box>
      );

    return (
      <Box>
        {paymentMethods.map((card) => (
          <CreditCardBox
            key={card.id}
            selected={selectedCard === card}
            onClick={handleCardSelect(card)}
            mt={2}>
            <Box {...flexCenter} gap={3}>
              <Radio checked={selectedCard === card} onChange={handleCardSelect(card)} />
              <Box>
                <Box {...flexCenter} justifyContent="start" columnGap={3}>
                  <Typography variant="h6">{card.name}</Typography>
                  {getCardIcon(card.brand as CardTypes)}
                  {card.default && <Chip label={DEFAULT_TEXT} color="success" size="small" />}
                </Box>
                <Typography variant="subtitle1" mt={1} fontWeight={600} fontFamily={SECONDARY_FONT}>
                  **** **** **** {card.last4}
                </Typography>
              </Box>
            </Box>
            <CardMenu cardId={card.id} handleSuccess={handleSuccess} isDefault={Boolean(card.default)} />
          </CreditCardBox>
        ))}
        <Box mt={2}>
          <Button variant="text" color="primary" size="large" onClick={openAddCardForm}>
            {CHANGE_PAYMENT_METHOD_TEXT}
          </Button>
        </Box>
      </Box>
    );
  },
);

export default ExistingCards;
